import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash-es';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getUserSelector } from '../../../../../../../../redux/reducers/reducerSlices/UserReducer';
import { sagaActions } from '../../../../../../../../sagas/sagaActions';
import { getPoSSselector } from '../../../../../../../../redux/reducers/reducerSlices/POSmartSchedulerReducer';
import { usePrevious } from '../../../../../../Common/CustomHooks/usePrevious';
import './AllAppointmentsListSS.css';
import moment from 'moment-timezone';
import DrilldownTableSS from '../../../../Dashboard/DefaultDashboard/DrillDownReports/Common/DrilldownTableSS';
import { getRemoteConfigSelector } from '../../../../../../../../redux/reducers/reducerSlices/RemoteConfigReducer';
import AvailableWindowDetails from './Components/AvailableWindowDetails';
import ModalSS from '../../../../../../SmartSchedule/SchedulePro/Components/Common/ModalSS/ModalSS';
import { shouldHavePermission } from '../../../../../../../Assests/Utility';
import ScheduleProWrapper from '../../../../../../SmartSchedule/SchedulePro/ScheduleProWrapper';

/**
 * DrillDownComplianceReport
 * Displays compliance report details for a specific Appointment Number.
 * Looks identical to ComplianceReport but:
 *  - Appointment Number column is plain text (no hyperlink)
 *  - No Actions column
 *
 * TODO: Wire up actual API once endpoint is provided.
 *       Replace the placeholder fetchAppts body with:
 *         dispatch({ type: sagaActions.GET_COMPLIANCE_DRILLDOWN_REPORT, payload: apiParams });
 *       where apiParams includes the apptNumber from useParams.
 */
const DrillDownComplianceReport = (props) => {
    const { themeSelected = 'light-mode' } = props;
    const { apptNumber } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { CarrierName: parentCarrierName, WarehouseNameWithExternalSiteID: parentWarehouseName } = location.state || {};
    const dispatch = useDispatch();
    const [allApptsArr, setAllApptsArr] = useState([]);
    const pOSS = useSelector(getPoSSselector);
    const { myAppointmentsList, arrangeOrder, arrangeKey, myAppointmentsListResponse, isLoading } = pOSS;
    const userObj = useSelector(getUserSelector);
    const { permissions } = userObj;
    const [currentTable, setCurrentTable] = useState(null);
    const [currentTableRef, setCurrentTableRef] = useState(null);
    const divRef = useRef(null);
    const [showLoading, setShowLoading] = useState(false);
    const previousTable = usePrevious(currentTable);
    const [showAvailableWindowModal, setShowAvailableWindowModal] = useState(false);
    const [selectedAvailableWindowData, setSelectedAvailableWindowData] = useState(null);
    const [allowPermission, setAllowPermission] = useState(false);
    const [permissionsLoaded, setPermissionsLoaded] = useState(false);

    // ─── Permissions ────────────────────────────────────────────────────────────
    useEffect(() => {
        if (permissions?.length) {
            const warehouseManagementPermission = _.find(permissions, { 'FeatureID': '12', 'PageAccess': true });
            if (warehouseManagementPermission && !_.isEmpty(warehouseManagementPermission)) {
                let temp = shouldHavePermission(permissions, '1000');
                setAllowPermission(temp);
            }
            setPermissionsLoaded(true);
        }
    }, [permissions]);

    // ─── Mount/Unmount ───────────────────────────────────────────────────────────
    useEffect(() => {
        dispatch({ type: sagaActions.RESET_APPTS_LIST_ACTION });
        return () => {
            dispatch({ type: sagaActions.RESET_APPTS_LIST_ACTION });
        };
    }, []);

    // ─── Response handler ────────────────────────────────────────────────────────
    useEffect(() => {
        if (myAppointmentsListResponse && myAppointmentsListResponse?.data?.length) {
            setShowLoading(false);
            setAllApptsArr(myAppointmentsListResponse.data);
        } else {
            setAllApptsArr([]);
            setShowLoading(false);
        }
    }, [myAppointmentsListResponse]);

    // ─── Fetch on mount (after permissions resolved) ──────────────────────────────
    useEffect(() => {
        if (!permissionsLoaded) return;
        fetchAppts();
    }, [permissionsLoaded]);

    // ─── Table ref tracker ───────────────────────────────────────────────────────
    useEffect(() => {
        const container = divRef.current;
        const tables = container.querySelectorAll('table');
        if (tables && tables.length && _.isEqual(currentTable, previousTable)) {
            setCurrentTableRef(container);
            setCurrentTable(tables);
        }
    });

    // ─── Fetch data ───────────────────────────────────────────────────────────────
    const fetchAppts = () => {
        setShowLoading(true);
        dispatch({
            type: sagaActions.GET_COMPLIANCE_HISTORY_FOR_REPORT,
            payload: { AppointmentNumber: apptNumber },
        });
    };

    // ─── Status colour helper ─────────────────────────────────────────────────────
    const cancelObj = {
        'Delete': 'Cancelled',
        'Re-Schedule': 'Rescheduled',
    };

    const getApptSchStatusColor = (str) => {
        if (str === 'Completed') {
            return <p className='text-success'>{str}</p>;
        } else if (str === 'Complete') {
            str = 'Completed';
            return <p className='text-success'>{str}</p>;
        } else if (str === 'Schedule') {
            str = 'Scheduled';
            return <p className='notScheStatusText'>{str}</p>;
        } else if (str === 'Delete') {
            return <p className='text-danger'>{cancelObj[str]}</p>;
        } else if (str === 'Re-Schedule') {
            return <p className='notScheStatusText'>{cancelObj[str]}</p>;
        } else {
            return <p className='notScheStatusText'>{str}</p>;
        }
    };

    // ─── Table column definitions (no Actions column) ────────────────────────────
    const apptsTitleDDWH = allowPermission
        ? [
            { id: 'serial_num', name: 'S.No' },
            { id: 'ApptNumber', name: 'Appt Number' },
            { id: 'PoNumber', name: 'PO/SO' },
            { id: 'carrier', name: 'Carrier' },
            { id: 'VendorName', name: 'Vendor' },
            { id: 'OutsideWindow', name: 'Outside Window' },
            { id: 'AvailableWindow', name: 'Available Window' },
            { id: 'WarehouseName', name: 'Warehouse' },
            { id: 'DockName', name: 'Dock' },
            { id: 'ApptStartTime', name: 'Appt Date & Time' },
            { id: 'DueDate', name: 'Due Date' },
            { id: 'TransDate', name: 'Created On' },
            { id: 'ApptScheStatus', name: 'Status' },
        ]
        : [
            { id: 'serial_num', name: 'S.No' },
            { id: 'ApptNumber', name: 'Appt Number' },
            { id: 'PoNumber', name: 'PO/SO' },
            { id: 'carrier', name: 'Carrier' },
            { id: 'VendorName', name: 'Vendor' },
            { id: 'OutsideWindow', name: 'Outside Window' },
            { id: 'AvailableWindow', name: 'Available Window' },
            { id: 'DockName', name: 'Dock' },
            { id: 'ApptStartTime', name: 'Appt Date & Time' },
            { id: 'DueDate', name: 'Due Date' },
            { id: 'TransDate', name: 'Created On' },
            { id: 'ApptScheStatus', name: 'Status' },
        ];

    // ─── Body data formatter (ApptNumber = plain text, no Actions) ───────────────
    const formatListDataDrillDown = (data) => {
        return data.map((el, index) => {
            const getWarehouseColumn = () => {
                let columns = {
                    id: el.RoleID,
                    0: index + 1,
                    1: el.AppointmentNumber,   // plain text — no hyperlink
                    2: el.PoNumber,
                    3: el?.CarrierName || parentCarrierName || '-',
                    4: el.VendorName || '-',
                    5: el.OutsideWindow ? 'Yes' : 'No',
                    6: el.OutsideWindow && el.AvailableWindow
                        ? (
                            <span>
                                Yes 
                            </span>
                            // <span
                            //     style={{ textDecoration: 'underline', color: 'blue', cursor: 'pointer' }}
                            //     onClick={() => {
                            //         setSelectedAvailableWindowData({
                            //             IDDock: el.IDDock,
                            //             IDWarehouse: el.IDWarehouse || el.WarehouseDetails?.ID,
                            //             PoNumber: el.PoNumber,
                            //             recommendationDetails: el.recommendationDetails || [],
                            //         });
                            //         setShowAvailableWindowModal(true);
                            //     }}
                            // >
                            //     Yes {el.recommendationDetails && el.recommendationDetails.length > 0
                            //         ? `(${el.recommendationDetails.length})`
                            //         : ''}
                            // </span>
                        )
                        : el.AvailableWindow ? 'Yes' : 'No',
                };

                if (allowPermission) {
                    columns[7] = el?.WarehouseNameWithExternalSiteID || parentWarehouseName || '-';
                    columns[8] = el.DockName || '-';
                    columns[9] = el.ApptStartTime
                        ? moment(el.ApptStartTime).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
                        : '-';
                    columns[10] = el.DueDate
                        ? moment(el.DueDate).utc().format('MM/DD/YYYY')
                        : '-';
                    columns[11] = el.ComplienceCreatedOn
                        ? moment(el.ComplienceCreatedOn).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
                        : '-';
                    columns[12] = getApptSchStatusColor(el.ApptScheStatus);
                } else {
                    columns[7] = el.DockName || '-';
                    columns[8] = el.ApptStartTime
                        ? moment(el.ApptStartTime).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
                        : '-';
                    columns[9] = el.DueDate
                        ? moment(el.DueDate).utc().format('MM/DD/YYYY')
                        : '-';
                    columns[10] = el.ComplienceCreatedOn
                        ? moment(el.ComplienceCreatedOn).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
                        : '-';
                    columns[11] = getApptSchStatusColor(el.ApptScheStatus);
                }

                return columns;
            };

            return getWarehouseColumn();
        });
    };

    const getBodyData = () => formatListDataDrillDown(allApptsArr);

    // ─── Render ──────────────────────────────────────────────────────────────────
    return (
        <ScheduleProWrapper showAppointments={false}>
            <div className='appointments-report-ss-wrapper p-3'>
                <div className='container-fluid createPo-wrapper-ss my-2 mx-3'>
                    {/* Header with back button */}
                    <div className='d-flex align-items-center gap-2 mb-2'>
                        <i
                            className='fa-solid fa-arrow-left pointer'
                            style={{ cursor: 'pointer' }}
                            onClick={() => navigate(-1)}
                            title='Go back'
                        />
                        <h4 className='mb-0'>
                            {'Compliance Report'}&nbsp;/&nbsp;
                            <span className='fw-normal'>{apptNumber}</span>
                        </h4>
                    </div>

                </div>

                {/* Table */}
                <div className='allApptsListContainer-ss' ref={divRef}>
                    <DrilldownTableSS
                        key={Math.random()}
                        themeSelected={themeSelected}
                        bodyData={getBodyData()}
                        titleData={apptsTitleDDWH}
                        showCollapse={false}
                        showInput={false}
                        showSort={false}
                        hasCollapsableContent={false}
                        collapsableData={[]}
                        accordionPrimaryIndex={1}
                        arrangeOrder={arrangeOrder}
                        arrangeKey={arrangeKey}
                        drillDownReport={myAppointmentsList}
                        initiateDrillDownReport={showLoading}
                        loaderArray={allowPermission ? 14 : 13}
                        sortingAction={sagaActions.SORT_SS_USER_TABLE}
                        subRoot={{ pathName: 'allApptsList' }}
                        showUTCTime={true}
                    />
                </div>

                {/* Available Window Modal */}
                {showAvailableWindowModal && (
                    <ModalSS toggleModal={() => setShowAvailableWindowModal(false)}>
                        <AvailableWindowDetails
                            themeSelected={themeSelected}
                            availableWindowData={selectedAvailableWindowData}
                            recommendationDetails={
                                selectedAvailableWindowData?.recommendationDetails || []
                            }
                        />
                    </ModalSS>
                )}

            </div>
        </ScheduleProWrapper>
    );
};

export default DrillDownComplianceReport;